/**
 * Support Center taxonomy — MIRRORS backend/src/config/supportTaxonomy.js.
 * Keep in sync. (Same convention as config/toolAccess.js and config/rolePermissions.js.)
 *
 * The labels here are the display copy; the backend also returns them from
 * GET /api/support-center/catalog. Prefer the catalog response at runtime — this file
 * exists so a component can render badges and empty states before the catalog has
 * loaded, without a flash of raw enum values.
 */

export const STATUS = {
  OPEN: 'open',
  ASSIGNED: 'assigned',
  IN_PROGRESS: 'in_progress',
  WAITING_FOR_CLIENT: 'waiting_for_client',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
};

export const STATUSES = Object.values(STATUS);
export const TERMINAL_STATUSES = [STATUS.RESOLVED, STATUS.CLOSED];

export const STATUS_LABELS = {
  [STATUS.OPEN]: 'Open',
  [STATUS.ASSIGNED]: 'Assigned',
  [STATUS.IN_PROGRESS]: 'In progress',
  [STATUS.WAITING_FOR_CLIENT]: 'Waiting for client',
  [STATUS.RESOLVED]: 'Resolved',
  [STATUS.CLOSED]: 'Closed',
};

/**
 * Badge classes. Uses the same `badge-*` utilities as the rest of the app
 * (see index.css) rather than inventing a parallel colour language.
 */
export const STATUS_BADGE = {
  [STATUS.OPEN]: 'bg-primary-50 text-primary-700 ring-1 ring-primary-200',
  [STATUS.ASSIGNED]: 'bg-brand-50 text-brand-700 ring-1 ring-brand-200',
  [STATUS.IN_PROGRESS]: 'bg-warning-50 text-warning-700 ring-1 ring-warning-200',
  [STATUS.WAITING_FOR_CLIENT]: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',
  [STATUS.RESOLVED]: 'bg-success-50 text-success-700 ring-1 ring-success-200',
  [STATUS.CLOSED]: 'bg-surface-100 text-surface-600 ring-1 ring-surface-200',
};

export const PRIORITY = { LOW: 'low', MEDIUM: 'medium', HIGH: 'high', URGENT: 'urgent' };
export const PRIORITIES = Object.values(PRIORITY);

export const PRIORITY_LABELS = {
  [PRIORITY.LOW]: 'Low',
  [PRIORITY.MEDIUM]: 'Medium',
  [PRIORITY.HIGH]: 'High',
  [PRIORITY.URGENT]: 'Urgent',
};

export const PRIORITY_BADGE = {
  [PRIORITY.LOW]: 'bg-surface-100 text-surface-600 ring-1 ring-surface-200',
  [PRIORITY.MEDIUM]: 'bg-primary-50 text-primary-700 ring-1 ring-primary-200',
  [PRIORITY.HIGH]: 'bg-warning-50 text-warning-700 ring-1 ring-warning-200',
  [PRIORITY.URGENT]: 'bg-error-50 text-error-700 ring-1 ring-error-200',
};

export const CATEGORY = {
  BUG: 'bug',
  TECHNICAL_ISSUE: 'technical_issue',
  FEATURE_REQUEST: 'feature_request',
  HOW_TO: 'how_to',
  DATA_ISSUE: 'data_issue',
  INTEGRATION: 'integration',
  BILLING: 'billing',
  ACCOUNT_ACCESS: 'account_access',
  OTHER: 'other',
};
export const CATEGORIES = Object.values(CATEGORY);

export const CATEGORY_LABELS = {
  [CATEGORY.BUG]: 'Bug / error',
  [CATEGORY.TECHNICAL_ISSUE]: 'Technical issue',
  [CATEGORY.FEATURE_REQUEST]: 'Feature request',
  [CATEGORY.HOW_TO]: 'How do I…',
  [CATEGORY.DATA_ISSUE]: 'Incorrect data',
  [CATEGORY.INTEGRATION]: 'Integration / sync',
  [CATEGORY.BILLING]: 'Billing',
  [CATEGORY.ACCOUNT_ACCESS]: 'Account & access',
  [CATEGORY.OTHER]: 'Other',
};

export const MESSAGE_KIND = {
  CLIENT_REPLY: 'client_reply',
  AGENT_REPLY: 'agent_reply',
  INTERNAL_NOTE: 'internal_note',
  SYSTEM: 'system',
};

export const SUPPORT_ROLE = { AGENT: 'agent', TEAM_LEAD: 'team_lead', SUPER_ADMIN: 'super_admin' };

export const SUPPORT_ROLE_LABELS = {
  [SUPPORT_ROLE.AGENT]: 'Team member',
  [SUPPORT_ROLE.TEAM_LEAD]: 'Team lead',
  [SUPPORT_ROLE.SUPER_ADMIN]: 'Super admin',
};

// STANDALONE: an app that owns its clients outside the portal's org model (DCC).
export const ORG_LINK_STATE = { LINKED: 'linked', UNLINKED: 'unlinked', INTERNAL: 'internal', STANDALONE: 'standalone' };

/** Human-readable activity lines for the timeline. */
export const ACTIVITY_LABELS = {
  created: 'Ticket created',
  status_changed: 'Status changed',
  assigned: 'Assigned',
  unassigned: 'Unassigned',
  priority_changed: 'Priority changed',
  category_changed: 'Category changed',
  sla_first_response_breached: 'First-response SLA breached',
  sla_resolution_breached: 'Resolution SLA breached',
  reopened: 'Reopened',
  closed: 'Closed',
  attachment_added: 'Attachment added',
  tenant_linked: 'Practice link updated',
  escalated: 'Priority escalated',
};

const toOptions = (values, labels) => values.map((value) => ({ value, label: labels[value] }));

export const STATUS_OPTIONS = toOptions(STATUSES, STATUS_LABELS);
export const PRIORITY_OPTIONS = toOptions(PRIORITIES, PRIORITY_LABELS);
export const CATEGORY_OPTIONS = toOptions(CATEGORIES, CATEGORY_LABELS);

/** Minutes → "2h 15m". Used for SLA chips and dashboard tiles. */
export function formatMinutes(minutes) {
  if (minutes === null || minutes === undefined) return '—';
  const total = Math.max(0, Math.round(minutes));
  if (total < 60) return `${total}m`;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours < 24) return mins ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours ? `${days}d ${remHours}h` : `${days}d`;
}

/** Relative time, kept short for table cells. */
export function timeAgo(value) {
  if (!value) return '—';
  const diffMs = Date.now() - new Date(value).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(value).toLocaleDateString();
}

/**
 * How overdue (or how much slack) a ticket has against its resolution target.
 * Negative `minutes` means still in hand.
 */
export function dueState(ticket) {
  if (!ticket?.resolutionDueAt || TERMINAL_STATUSES.includes(ticket.status)) {
    return { state: 'none', minutes: null };
  }
  const minutes = Math.round((Date.now() - new Date(ticket.resolutionDueAt).getTime()) / 60000);
  if (minutes > 0) return { state: 'breached', minutes };
  if (minutes > -120) return { state: 'at_risk', minutes };
  return { state: 'ok', minutes };
}
